import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useMember } from '@/lib/members/hooks/useMember.ts';
import Loading from '@/components/common/loading.tsx';
import { useEffect, useState } from 'react';
import { isProfileComplete } from '@/lib/members/member-status.ts';
import { usePaymentMethods } from '@/lib/payment-methods/hooks/usePaymentMethods.ts';
import useClubs from '@/lib/clubs/hooks/useClubs.ts';
import { toast } from 'sonner';

/**
 * This route is responsible for redirecting to the profile page if it's not complete
 */
export const Route = createFileRoute(
  '/_checkAuth/_layout/_authenticated/_member',
)({
  component: CheckIsMemberRoute,
});

export function CheckIsMemberRoute() {
  const [checked, setChecked] = useState(false);
  const { member } = useMember();
  const { paymentMethods, isFetched: paymentMethodsFetched } =
    usePaymentMethods();
  const { currentClub, isFetched: clubsFetched } = useClubs();
  const requiresCreditCard = !currentClub || currentClub.requiresCreditCard;
  const navigate = Route.useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const check = async () => {
      console.debug('Checking if the user is a member...');

      const hasMembership = !!member?.MemberId;

      if (!hasMembership) {
        console.debug('User is not a member, redirecting to join page...');

        return await navigate({
          to: '/asociate',
          replace: true,
        });
      }

      console.debug('User is a member!');

      const isOnMemberRoute = pathname.startsWith('/miembros');
      const isOnProfile = pathname === '/miembros/perfil';
      const isOnPayment = pathname === '/miembros/medio-de-pago';

      // If navigating away from member routes (e.g. logout), skip checks
      if (!isOnMemberRoute) return;

      // Check profile completion (skip if already on profile page)
      if (!isOnProfile && !isProfileComplete(member)) {
        console.debug(
          'User profile is incomplete, redirecting to profile page...',
        );
        toast.warning('Completá tu perfil para continuar.');

        return await navigate({
          to: '/miembros/perfil',
          replace: true,
        });
      }

      // Check payment method (skip if on profile or payment page, or if not yet fetched)
      if (
        !isOnProfile &&
        !isOnPayment &&
        clubsFetched &&
        requiresCreditCard &&
        paymentMethodsFetched &&
        paymentMethods.length === 0
      ) {
        console.debug(
          'User does not have a payment method, redirecting to payment methods page...',
        );
        toast.warning('Agregá un medio de pago para continuar.');

        return await navigate({
          to: '/miembros/medio-de-pago',
          replace: true,
        });
      }

      console.debug('User has complete profile and payment method!');
    };

    // Wait for both clubs and payment methods to be fetched before checking
    if (!clubsFetched || !paymentMethodsFetched) {
      console.debug('Clubs or payment methods not yet fetched, waiting...');
      return;
    }

    check().finally(() => {
      setChecked(true);
    });
  }, [member, navigate, pathname, paymentMethods, paymentMethodsFetched, currentClub, clubsFetched]);

  if (!checked) {
    return <Loading />;
  }

  return <Outlet />;
}
